import useIncomingCourseMessages from "../CourseRealtime/useIncomingCourseMessages";
import { api } from "../../config/api";
import { coursesEnabled, courseApiEnabled } from "../../config/courseFeatures";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import socketIO from "socket.io-client";
import { IoClose } from "react-icons/io5";
import { useThunk } from "../../hooks/useThunk";
import { fetchThreadById, setSelectedFormType } from "../../store";
import { CONFIGURATIONS } from "../../config/envConfig";
import { getTokenPayload } from "../../utils/getTokenPayload";
import showToast from "../../utils/toastNotifications";
import { refreshInProgressOrderCount } from "../Header/useInProgressOrderCount";

const UNREAD_THREAD_COUNT_REFRESH_EVENT = "unread-thread-count-refresh";

const IncomingMessagePrompt = ({ ordersEnabled = true }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tokenPayload = getTokenPayload();
  const socketRef = useRef(null);
  const lastSoundAtRef = useRef(0);
  const [promptMessages, setPromptMessages] = useState([]);
  const [openingCourse, setOpeningCourse] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [doFetchThreadById, isFetchThreadById] = useThunk(fetchThreadById);
  const promptMessage = promptMessages[promptMessages.length - 1] || null;
  const moreMessagesCount = Math.max(promptMessages.length - 1, 0);

  const playNotificationSound = () => {
    const now = Date.now();

    if (now - lastSoundAtRef.current < 3000) {
      return;
    }

    lastSoundAtRef.current = now;

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;

      if (!AudioContext) {
        return;
      }

      const audioContext = new AudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(660, audioContext.currentTime + 0.08);
      gainNode.gain.setValueAtTime(0.0001, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.08, audioContext.currentTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.22);

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.24);
      oscillator.onended = () => audioContext.close();
    } catch (error) {
      // Browsers can block sound before user interaction; the visual prompt still works.
    }
  };

  useIncomingCourseMessages({
    enabled: coursesEnabled && courseApiEnabled,
    role: "customer",
    readerId: tokenPayload?._id,
    onMessage: message => {
      setPromptMessages(previous => [...previous, message].slice(-5));
      playNotificationSound();
    },
    onViewed: courseId => setPromptMessages(previous => previous.filter(message => message.type !== "course" || message.courseId !== courseId)),
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const clearOldSession = () => { if (localStorage.getItem("token") !== token) setPromptMessages([]); };
    window.addEventListener("storage", clearOldSession);
    window.addEventListener("focus", clearOldSession);
    return () => { window.removeEventListener("storage", clearOldSession); window.removeEventListener("focus", clearOldSession); };
  }, [tokenPayload?._id]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!ordersEnabled || !token || !tokenPayload?._id || socketRef.current) {
      return;
    }

    const socket = socketIO.connect(CONFIGURATIONS.API_BASE_DOMAIN, {
      auth: {
        token,
      },
    });

    const joinRooms = () => {
      socket.emit("join-room", {
        userId: tokenPayload?._id || "",
        role: tokenPayload?.roles ? tokenPayload?.roles[0] : "",
        brand: CONFIGURATIONS.CLIENT_APP_BRAND,
      });
    };

    const getSenderLabel = (message) => {
      if (message?.isAdmin) {
        return "Admin";
      }

      if (message?.senderModel === "Writer") {
        return "Writer";
      }

      return "your order thread";
    };

    const handleIncomingMessage = async (message) => {
      const isOwnMessage =
        message?.userId?.toString() === tokenPayload?._id?.toString();

      if (isOwnMessage || !message?.roomId) {
        return;
      }

      window.dispatchEvent(
        new CustomEvent(UNREAD_THREAD_COUNT_REFRESH_EVENT, {
          detail: { threadId: message.roomId },
        })
      );
      refreshInProgressOrderCount();

      let threadData = null;

      try {
        const result = await doFetchThreadById({ roomId: message.roomId });
        threadData = result?.success ? result?.response?.data : null;
      } catch (error) {
        threadData = null;
      }

      if (socketRef.current !== socket || localStorage.getItem("token") !== token) return;

      const nextPromptMessage = {
        threadId: message.roomId,
        routeThreadId: threadData?._id || message.roomId,
        orderRoomId: threadData?.roomId || "",
        senderLabel: getSenderLabel(message),
        content: message?.text || "New message received",
        createdAt: message?.createdAt,
      };

      setPromptMessages((prevMessages) => [...prevMessages, nextPromptMessage].slice(-5));
      playNotificationSound();
    };

    socketRef.current = socket;
    socket.on("connect", joinRooms);
    socket.on("messageResponse", handleIncomingMessage);

    if (socket.connected) {
      joinRooms();
    }

    return () => {
      socket.off("connect", joinRooms);
      socket.off("messageResponse", handleIncomingMessage);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [doFetchThreadById, tokenPayload?._id, ordersEnabled]);

  const handleOpenThread = async () => {
    if (promptMessage?.type === "course") {
      if (openingCourse) return;
      const token = localStorage.getItem("token");
      setOpeningCourse(true);
      try {
        await api.get(promptMessage.courseBase);
        if (!mounted.current || !token || localStorage.getItem("token") !== token) return;
        setPromptMessages(previous => previous.filter(message => message.type !== "course" || message.courseId !== promptMessage.courseId));
        navigate(promptMessage.courseRoute);
      } catch (error) {
        if (mounted.current && localStorage.getItem("token") === token) showToast("error", error.message || "This course is no longer available.");
      } finally { if (mounted.current) setOpeningCourse(false); }
      return;
    }
    if (!promptMessage?.threadId) {
      return;
    }

    let orderRoomId = promptMessage.orderRoomId;
    let threadId = promptMessage.routeThreadId || promptMessage.threadId;

    if (!orderRoomId) {
      const result = await doFetchThreadById({ roomId: promptMessage.threadId });

      if (!result?.success) {
        showToast("error", result?.error?.message || "Unable to open thread");
        return;
      }

      const selectedThread = result?.response?.data;
      orderRoomId = selectedThread?.roomId;
      threadId = selectedThread?._id || promptMessage.threadId;
    }

    if (!orderRoomId || !threadId) {
      showToast("error", "Unable to open thread");
      return;
    }

    dispatch(setSelectedFormType("message"));
    setPromptMessages([]);
    navigate(`/thread?roomIdParam=${orderRoomId}&threadIdParam=${threadId}`);
  };

  const dismissLatestPrompt = () => {
    setPromptMessages((prevMessages) => prevMessages.slice(0, -1));
  };

  if (!promptMessage) {
    return null;
  }

  return (
    <div className="incoming-message-alert fixed bottom-4 right-4 z-[70] w-[calc(100vw-2rem)] max-w-[380px] overflow-hidden rounded-[14px] border-2 border-blue-shades bg-white shadow-[0_18px_48px_rgba(15,23,42,0.16)]">
      <div className="flex items-center justify-between gap-3 bg-blue-shades px-4 py-2.5 text-white">
        <div className="flex min-w-0 items-center gap-2">
          <span className="relative flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75"></span>
            <span className="relative inline-flex h-3 w-3 rounded-full bg-white"></span>
          </span>
          <div className="min-w-0">
            <p className="font-inter text-[13px] font-bold leading-5">
              New message from {promptMessage.senderLabel}
            </p>
            {(promptMessage.courseId || promptMessage.orderRoomId) && (
              <p className="font-inter text-[11px] font-semibold leading-4 text-white/90">
                {promptMessage.type === "course" ? `Course ${promptMessage.courseId}` : `Order ${promptMessage.orderRoomId}`}
              </p>
            )}
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-white px-2.5 py-1 font-inter text-[10px] font-bold uppercase leading-3 tracking-normal text-blue-shades">
          {moreMessagesCount > 0 ? `+${moreMessagesCount}` : "New"}
        </span>
      </div>
      <div className="flex items-start gap-3 p-4">
        <div className="min-w-0 flex-1">
          <p className="mt-1 line-clamp-2 break-words font-inter text-[13px] leading-5 text-gray">
            {promptMessage.content}
          </p>
          {moreMessagesCount > 0 && (
            <p className="mt-2 font-inter text-[12px] font-semibold leading-4 text-blue-shades">
              {moreMessagesCount} more new {moreMessagesCount === 1 ? "message" : "messages"}
            </p>
          )}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={handleOpenThread}
              disabled={promptMessage.type === "course" ? openingCourse : isFetchThreadById}
              className="rounded-[8px] bg-blue-shades px-3 py-2 font-inter text-[12px] font-semibold text-white transition-opacity disabled:opacity-70"
            >
              {promptMessage.type === "course" ? openingCourse ? "Opening..." : "Open course" : isFetchThreadById ? "Opening..." : "Open Thread"}
            </button>
            <button
              type="button"
              onClick={dismissLatestPrompt}
              className="rounded-[8px] bg-light-blue px-3 py-2 font-inter text-[12px] font-semibold text-blue-shades"
            >
              Dismiss
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismissLatestPrompt}
          className="rounded-full p-1 text-secondary hover:bg-soft-gray"
          aria-label="Dismiss message prompt"
        >
          <IoClose className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default IncomingMessagePrompt;
